import { useState, useEffect } from 'react';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogTitle,
	DialogDescription,
} from '@uconnect/assets/components/ui/dialog';
import { cn } from '@uconnect/assets/lib/utils';
import { Button } from '@uconnect/assets/components/ui/button';
import Icon from '@uconnect/assets/components/ui/icon';
import AgentUI from './AgentUI';
import RuntimeProvider, { useNewChat } from './RuntimeProvider';
import useSearchQuota from './useSearchQuota';
import type { AgentUIConfig } from './types';
import useKeyboardShortcut from './hooks/useKeyboardShortcut';
import useAuthSync from './hooks/useAuthSync';
import { useAgentOpenedEvent } from './hooks/useAgentAnalytics';

interface AgentSidebarProps {
	config?: AgentUIConfig;
}

const POST_LOGIN_FLAG = 'uc-agent-post-login';
const AUTH_PATHS = [ '/account/login', '/account/signup', '/account/reset-password', '/account/verify' ];

// `config.fabIcon` is already resolved to an Icon component name (icon.tsx) server-side — see
// SearchAgentCustomization::getLauncherIconReactName() and student-agent.php — so it can be used directly here.
const DEFAULT_FAB_ICON_NAME = 'aiSearch';

function isAuthPage(): boolean {
	const path = window.location.pathname;
	return AUTH_PATHS.some( ( p ) => path === p || path.startsWith( p + '/' ) );
}

export default function AgentSidebar( { config = {} }: AgentSidebarProps ) {
	const [ open, setOpen ] = useState( false );
	const [ isExpanded, setIsExpanded ] = useState( false );
	const searchPositionLeft = config.searchPosition === 'left';

	// Close the panel and drop the previous conversation when the session ends in any
	// tab. Must run before the reopen effect below, so the thread id is gone before
	// `open` can flip and RuntimeProvider mounts and reads it.
	const { loggedOut } = useAuthSync( {
		userId: config.userId,
		onLogout: () => setOpen( false ),
	} );

	// Reopen panel after a login initiated from within it
	useEffect( () => {
		if ( config.isLoggedIn && sessionStorage.getItem( POST_LOGIN_FLAG ) === 'true' && ! isAuthPage() ) {
			sessionStorage.removeItem( POST_LOGIN_FLAG );
			setOpen( true );
		}
	}, [] ); // eslint-disable-line react-hooks/exhaustive-deps

	// Handle keyboard shortcut (Ctrl/Cmd + K)
	useKeyboardShortcut( {
		onToggle: () => setOpen( ( prev ) => ! prev ),
		enabled: true,
	} );

	useAgentOpenedEvent( {
		isOpen: open,
		isLoggedIn: config.isLoggedIn ?? false,
		siteId: config.siteId,
	} );

	useEffect( () => {
		if ( ! open ) {
			return undefined;
		}
		const handleEscKeyDown = ( event: KeyboardEvent ) => {
			if ( event.key === 'Escape' ) {
				setOpen( false );
			}
		};
		document.addEventListener( 'keydown', handleEscKeyDown );
		return () => {
			document.removeEventListener( 'keydown', handleEscKeyDown );
		};
	}, [ open ] );

	// Focus the text input when dialog opens
	useEffect( () => {
		if ( open ) {
			// Use a small delay to ensure the dialog is fully rendered
			const timer = setTimeout( () => {
				const input = document.querySelector( '.agent-composer-input' ) as HTMLTextAreaElement;
				if ( input ) {
					input.focus();
				}
			}, 100 );
			return () => clearTimeout( timer );
		}

		return undefined;
	}, [ open ] );

	// Support links like <a href="#launch-ai-search"> from menus/widgets/content
	useEffect( () => {
		const handleLaunchClick = ( event: Event ) => {
			event.preventDefault();
			setOpen( true );
		};

		const handleLaunchLinkKeyDown = ( event: Event ) => {
			const keyEvent = event as KeyboardEvent;
			if ( keyEvent.key === 'Enter' || keyEvent.key === ' ' ) {
				event.preventDefault();
				setOpen( true );
			}
		};

		const launchLinks = document.querySelectorAll( 'a[href="#launch-ai-search"]' );
		launchLinks.forEach( ( link ) => {
			link.setAttribute( 'role', 'button' );
			link.setAttribute( 'aria-haspopup', 'dialog' );
			link.addEventListener( 'click', handleLaunchClick );
			link.addEventListener( 'keydown', handleLaunchLinkKeyDown );
		} );

		return () => {
			launchLinks.forEach( ( link ) => {
				link.removeEventListener( 'click', handleLaunchClick );
				link.removeEventListener( 'keydown', handleLaunchLinkKeyDown );
			} );
		};
	}, [] );

	// Ensure "Skip to AI Search" link moves keyboard focus to FAB.
	useEffect( () => {
		const focusFab = () => {
			const fab = document.getElementById( 'launch-ai-search' ) as HTMLButtonElement | null;
			if ( fab ) {
				fab.focus( { preventScroll: true } );
			}
		};

		const handleSkipClick = ( event: Event ) => {
			event.preventDefault();
			focusFab();
		};

		const skipLinks = document.querySelectorAll( 'a[href="#launch-ai-search"]' );
		skipLinks.forEach( ( link ) => {
			link.addEventListener( 'click', handleSkipClick );
		} );

		// If hash navigation is used directly, still transfer focus to the FAB.
		if ( window.location.hash === '#launch-ai-search' ) {
			window.setTimeout( focusFab, 0 );
		}

		return () => {
			skipLinks.forEach( ( link ) => {
				link.removeEventListener( 'click', handleSkipClick );
			} );
		};
	}, [] );

	const sidebarConfig: AgentUIConfig = {
		...config,
		// `config` is a page-load snapshot; after a logout elsewhere it is stale, so the
		// reopened panel must render as signed out (no search history, sign-in prompts).
		isLoggedIn: Boolean( config.isLoggedIn ) && ! loggedOut,
		userId: loggedOut ? 0 : config.userId,
		title: config.title || 'Search Career Content',
		context: 'studentSidebar',
		isExpanded,
		onSignIn: () => sessionStorage.setItem( POST_LOGIN_FLAG, 'true' ),
		toasterProps: {
			position: 'top-right',
			containerAriaLabel: 'Career Content Notifications',
			...config.toasterProps,
		},
		containerMaxWidthClasses: 'w-full max-w-2xl mx-auto!',
	};

	return (
		<>
			{ ! open && (
				<button
					type="button"
					id="launch-ai-search"
					data-uc-agent-fab="true"
					data-style={ config.fabStyle || undefined }
					data-effect={ config.fabEffect || undefined }
					data-fab-icon={ config.fabIcon || undefined }
					className={ cn(
						'default !fixed !bottom-8 !z-[960] !m-0 !inline-flex !h-14 !w-14 !items-center !justify-center rounded-2xl !border-2 border-solid border-white bg-primary !p-0 text-primary-foreground !leading-none shadow-md hover:shadow-lg !cursor-pointer transition! hover:!scale-110 hover:bg-primary-light focus-visible:!outline-2 focus-visible:!outline-white focus-visible:!-outline-offset-2 motion-reduce:!transition-none motion-reduce:hover:!scale-100',
						searchPositionLeft ? '!left-8' : '!right-8',
					) }
					onClick={ () => setOpen( true ) }
					aria-label="Open AI Search"
					aria-haspopup="dialog"
					title="Open AI Search"
				>
					<Icon
						name={ config.fabIcon || DEFAULT_FAB_ICON_NAME }
						className="!h-6 !w-6"
					/>
				</button>
			) }
			<Dialog open={ open } onOpenChange={ setOpen }>
				<DialogContent
					variant="panel"
					aria-modal={ true }
					className={ cn(
						'grid [grid-template-rows:max-content_1fr] bg-white!',
						'p-0! gap-0! top-0 rounded-none! custom-scrollbar-container scrollbar--light',
						searchPositionLeft
							? 'left-0 motion-safe:data-[state=open]:slide-in-from-left motion-safe:data-[state=closed]:slide-out-to-left'
							: 'right-0 motion-safe:data-[state=open]:slide-in-from-right motion-safe:data-[state=closed]:slide-out-to-right',
						'motion-safe:transition-[width] duration-300 ease-in-out',
						'w-dvw',
						! isExpanded && 'sm:w-lg',
					) }
					showCloseButton={ false } // Manually added below due to placement
				>
				<RuntimeProvider config={ sidebarConfig }>
					<SidebarHeaderWrapper
						config={ sidebarConfig }
						title={ sidebarConfig.title }
						isExpanded={ isExpanded }
						setIsExpanded={ setIsExpanded }
					/>
					<DialogDescription className="sr-only">
						AI-powered search assistant for career content
					</DialogDescription>
					{ open && <AgentUI config={ sidebarConfig } /> }
				</RuntimeProvider>
				</DialogContent>
			</Dialog>
		</>
	);
}

function SidebarHeader( {
	config,
	title,
	isExpanded,
	setIsExpanded,
	remainingSearches,
}: {
	config: AgentUIConfig;
	title: string;
	isExpanded: boolean;
	setIsExpanded: ( value: boolean ) => void;
	remainingSearches: number;
} ) {
	const { hasMessages, startNewChat } = useNewChat();
	const chatDisabled = !config.isLoggedIn && remainingSearches <= 0;
	const searchPositionLeft = config.searchPosition === 'left';

	const closeButton = (
		<Button
			variant="mutedInterface"
			size="icon"
			className={ cn(
				'flex-shrink-0 size-6',
				! searchPositionLeft && 'mr-auto!',
			) }
			asChild
		>
			<DialogClose data-slot="dialog-close">
				<Icon name="xmark" className="pointer-events-none shrink-0 size-5" />
				<span className="sr-only">Close</span>
			</DialogClose>
		</Button>
	);

	const expandButton = (
		<Button
			variant="mutedInterface"
			size="icon"
			onClick={ () => setIsExpanded( ! isExpanded ) }
			className="flex-shrink-0 hidden! sm:flex! size-6"
			aria-label={
				isExpanded
					? 'Contract panel to sidebar mode'
					: 'Expand panel to full-screen mode'
			}
		>
			<Icon
				name={ isExpanded ? ( searchPositionLeft ? 'doubleChevronLeft' : 'doubleChevronRight' ) : 'expand' }
				className="!size-5"
			/>
		</Button>
	);

	return (
		<div
			className={ cn(
				'p-3! flex items-center justify-end gap-4 bg-white! transition-all',
				config.containerMaxWidthClasses
			) }
		>
			{ searchPositionLeft ? expandButton : closeButton }

			<DialogTitle className="m-0! text-base! flex-grow sr-only sm:not-sr-only">
				{ title }
			</DialogTitle>

			{ hasMessages && (
				<Button
					variant="link"
					size="sm"
					className="default font-default! px-1! py-0.5! text-sm! normal-case h-auto!"
					onClick={ startNewChat }
					disabled={ chatDisabled }
				>
					New Search
				</Button>
			) }

			{ ! config.isLoggedIn && (
				<Button
					asChild
					variant="link"
					className="px-1! py-0.5! text-sm! normal-case! h-auto!"
				>
					<a href={ config.signInUrl }>Sign In</a>
				</Button>
			) }

			{ searchPositionLeft ? closeButton : expandButton }
		</div>
	);
}

function SidebarHeaderWrapper( {
	config,
	title,
	isExpanded,
	setIsExpanded,
}: {
	config: AgentUIConfig;
	title: string;
	isExpanded: boolean;
	setIsExpanded: ( value: boolean ) => void;
} ) {
	const { remainingSearches } = useSearchQuota( Boolean( config.isLoggedIn ) );

	return (
		<SidebarHeader
			config={ config }
			title={ title }
			isExpanded={ isExpanded }
			setIsExpanded={ setIsExpanded }
			remainingSearches={ remainingSearches }
		/>
	);
}
